import { useCallback, useEffect, useRef, useState } from 'react';
import { energyService } from '../services/energyService';
import { logger } from '../utils/logger';

// Energy tank state (Energy HUB part 1).
//
// Lives as a hook rather than inside EnergyPanel because the LOCKDOWN state is
// not a panel concern — when the tank is empty the chat composer and uploads
// must refuse too. That wiring belongs to a later slice (it touches
// MainOfficeView); this hook is the seam it will consume, so the lock is read
// from ONE place instead of two components fetching /tank independently.

// Fired by anything that knows the tank moved (a completed turn, a top-up).
// Nothing dispatches it yet — the panel just listens, following the established
// `omnis:*` window-event bus.
export const ENERGY_UPDATED_EVENT = 'omnis:energy-updated';

/**
 * Is PAI locked out for lack of energy?
 *
 * Pure and defensive: an ABSENT tank (endpoint not deployed yet, request in
 * flight, request failed) is NOT locked — a monitoring outage must never be
 * able to lock a paying user out of their assistant.
 */
export const isEnergyLocked = (tank) => !!tank?.locked;

export default function useEnergyTank(enabled = true) {
    const [tank, setTank] = useState(null);
    const [loading, setLoading] = useState(!!enabled);
    const [error, setError] = useState(null);

    // Guards a setState after unmount, and drops a stale response that lands
    // after a newer one (last-ISSUED-wins — the #294 lesson).
    const aliveRef = useRef(true);
    const reqRef = useRef(0);
    // 🛑 The flag MUST be re-armed on (re)mount, not only cleared on unmount.
    // React 18 StrictMode mounts → cleans up → re-mounts in development, so a
    // cleanup-only version latches `false` on the very first pass and then drops
    // EVERY response for the lifetime of the component: the panel renders its
    // em-dash placeholders forever and never shows a number. Production builds
    // don't double-invoke, so this only bites in `npm run dev` — which is also
    // exactly what the E2E suite runs against.
    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    const reload = useCallback(async () => {
        if (!enabled) return;
        const ticket = ++reqRef.current;
        setLoading(true);
        try {
            const data = await energyService.getTank();
            if (!aliveRef.current || ticket !== reqRef.current) return;
            setTank(data || null);
            setError(null);
        } catch (err) {
            if (!aliveRef.current || ticket !== reqRef.current) return;
            logger.warn('ENERGY', `tank load failed: ${err?.message || err}`);
            setError(err);
        } finally {
            if (aliveRef.current && ticket === reqRef.current) setLoading(false);
        }
    }, [enabled]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        if (!enabled) return undefined;
        const onUpdate = () => reload();
        window.addEventListener(ENERGY_UPDATED_EVENT, onUpdate);
        return () => window.removeEventListener(ENERGY_UPDATED_EVENT, onUpdate);
    }, [enabled, reload]);

    return { tank, loading, error, reload, locked: isEnergyLocked(tank) };
}
